// Three.js hero backdrop: a flowing point-cloud "liquidity surface" tinted
// with the brand gradient, plus gentle mouse parallax. Honors
// prefers-reduced-motion (single static frame) and pauses when the tab hides.
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  Fog,
  NormalBlending,
  PerspectiveCamera,
  Points,
  PointsMaterial,
  Scene,
  WebGLRenderer,
} from 'three';
import { find } from '../core/dom';

const COLS = 150;
const ROWS = 70;
const SPREAD_X = 46;
const SPREAD_Z = 34;

const STOPS = [new Color('#0a84ff'), new Color('#bf5af2'), new Color('#ff375f')];

function rampColor(t: number, out: Color): void {
  const scaled = Math.min(0.999, Math.max(0, t)) * (STOPS.length - 1);
  const i = Math.floor(scaled);
  out.copy(STOPS[i]!).lerp(STOPS[i + 1]!, scaled - i);
}

/** Soft round sprite so points render as glow dots instead of squares. */
function makeSprite(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.4, 'rgba(255,255,255,0.5)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  return new CanvasTexture(canvas);
}

function surfaceHeight(x: number, z: number, time: number): number {
  return (
    Math.sin(x * 0.32 + time * 0.9) * 0.55 +
    Math.sin(z * 0.5 - time * 0.7) * 0.45 +
    Math.sin((x + z) * 0.21 + time * 0.45) * 0.7 +
    Math.sin(Math.hypot(x, z) * 0.42 - time * 1.1) * 0.25
  );
}

export function initHeroScene(): void {
  const canvas = find<HTMLCanvasElement>('heroScene');
  if (!canvas) return;

  const renderer = new WebGLRenderer({ canvas, alpha: true, antialias: false, powerPreference: 'low-power' });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));

  const scene = new Scene();
  scene.fog = new Fog(0x050508, 18, 46);

  const camera = new PerspectiveCamera(58, 1, 0.1, 100);
  camera.position.set(0, 5.4, 15);

  const geometry = new BufferGeometry();
  const count = COLS * ROWS;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const i = r * COLS + c;
      positions[i * 3] = (c / (COLS - 1) - 0.5) * SPREAD_X;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = (r / (ROWS - 1) - 0.72) * SPREAD_Z;
    }
  }
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('color', new BufferAttribute(colors, 3));

  const material = new PointsMaterial({
    size: 0.42,
    map: makeSprite(),
    vertexColors: true,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  scene.add(new Points(geometry, material));

  // Additive glow reads beautifully on dark; on light surfaces switch to
  // normal blending and calm the opacity so the copy stays legible.
  const applySceneTheme = (): void => {
    const dark = document.documentElement.dataset.theme !== 'light';
    material.blending = dark ? AdditiveBlending : NormalBlending;
    material.opacity = dark ? 0.85 : 0.5;
    material.needsUpdate = true;
    scene.fog = new Fog(dark ? 0x050508 : 0xf4f5f8, 18, 46);
  };
  applySceneTheme();
  document.addEventListener('amaga:theme', applySceneTheme);

  const tint = new Color();
  const updateSurface = (time: number): void => {
    for (let i = 0; i < count; i++) {
      const x = positions[i * 3]!;
      const z = positions[i * 3 + 2]!;
      const y = surfaceHeight(x, z, time);
      positions[i * 3 + 1] = y;
      const depth = 1 - Math.min(1, Math.abs(z + 12) / SPREAD_Z);
      rampColor((y + 1.6) / 3.4, tint);
      const fade = 0.35 + depth * 0.65;
      colors[i * 3] = tint.r * fade;
      colors[i * 3 + 1] = tint.g * fade;
      colors[i * 3 + 2] = tint.b * fade;
    }
    geometry.getAttribute('position').needsUpdate = true;
    geometry.getAttribute('color').needsUpdate = true;
  };

  const host = canvas.parentElement!;
  const resize = (): void => {
    const { clientWidth, clientHeight } = host;
    if (clientWidth === 0 || clientHeight === 0) return;
    renderer.setSize(clientWidth, clientHeight, false);
    camera.aspect = clientWidth / clientHeight;
    camera.updateProjectionMatrix();
  };
  new ResizeObserver(resize).observe(host);
  resize();

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) {
    updateSurface(2);
    renderer.render(scene, camera);
    return;
  }

  let targetX = 0;
  let targetY = 0;
  addEventListener('pointermove', (e) => {
    targetX = (e.clientX / innerWidth - 0.5) * 1.6;
    targetY = (e.clientY / innerHeight - 0.5) * 0.8;
  }, { passive: true });

  let frameId = 0;
  const start = performance.now();
  const loop = (): void => {
    const time = (performance.now() - start) / 1000;
    updateSurface(time * 0.7);
    camera.position.x += (targetX - camera.position.x) * 0.03;
    camera.position.y += (5.4 - targetY - camera.position.y) * 0.03;
    camera.lookAt(0, 0.4, -6);
    renderer.render(scene, camera);
    frameId = requestAnimationFrame(loop);
  };
  loop();

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) cancelAnimationFrame(frameId);
    else loop();
  });
}
